'use client'

import { AssistantAvatar } from '@/components/AssistantAvatar'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { API_ONBOARDING_WELCOME_ACCEPTED_URL } from '@/consts/baseUrls'
import axios from '@/utils/axios'
import { logClickEvent } from '@/utils/eventLogging'
import { Clock, Info, ShieldAlert } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

const STORAGE_KEY = 'w_w_d' // walter_welcome_dismissed
const STATIC_PATHS = ['/terms', '/privacy', '/imprint']

interface InstantUserWelcomeDialogProps {
    isInstantUser: boolean
}

export function InstantUserWelcomeDialog({ isInstantUser }: InstantUserWelcomeDialogProps) {
    const pathname = usePathname()
    const [isOpen, setIsOpen] = useState(false)
    const isStaticPage = STATIC_PATHS.some((p) => pathname.startsWith(p))

    useEffect(() => {
        if (!isInstantUser || isStaticPage) return
        const dismissed = sessionStorage.getItem(STORAGE_KEY)
        if (!dismissed) {
            const setOpen = () => {
                setIsOpen(true)
            }
            setOpen()
        }
    }, [isInstantUser, isStaticPage])

    const handleAccept = () => {
        logClickEvent({ action: 'instant_user_welcome_accept' })
        sessionStorage.setItem(STORAGE_KEY, 'true')
        setIsOpen(false)

        // Persist the confirmation so a bare route hit stays distinguishable from a real start.
        // Fire-and-forget: a failed write must not block the user from chatting.
        void axios.post(API_ONBOARDING_WELCOME_ACCEPTED_URL, {}, { withCredentials: true }).catch(() => {})
    }

    return (
        <AlertDialog open={isOpen}>
            <AlertDialogContent
                className="!border-white/10 !bg-[#1e2330] text-white shadow-2xl sm:max-w-xl sm:rounded-2xl"
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <AlertDialogHeader>
                    <div className="mx-auto mb-2 h-16 w-16 overflow-hidden rounded-full ring-2 ring-primary/30 [&_img]:!h-full [&_img]:!w-full [&_img]:!rounded-full">
                        <AssistantAvatar />
                    </div>
                    <AlertDialogTitle className="text-center text-xl">
                        Willkommen bei Frage Walter Match!
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-center text-sm text-gray-300">
                        Schön, dass du da bist. Bevor es losgeht, ein paar kurze Hinweise.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-4 py-2">
                    {/* What Walter Match does */}
                    <div className="flex gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                        <div className="text-sm text-gray-200">
                            <p className="mb-1 font-semibold text-white">Dein persönlicher KI-Berufscoach</p>
                            <p>
                                Sprich mit Walter über deine Interessen, Stärken oder Berufe, die dich interessieren. So
                                findest du <strong>Berufe und Unternehmen</strong>, die gut zu dir passen -{' '}
                                <strong>einfach und kostenlos</strong>.
                            </p>
                        </div>
                    </div>

                    {/* Temporary session info */}
                    <div className="flex gap-3 rounded-lg border border-secondary/20 bg-secondary/5 p-3">
                        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                        <p className="text-sm text-gray-200">
                            Dein Chat wird nicht gespeichert. Mit einem kostenlosen Account kannst du deine Gespräche
                            behalten und später einfach weitermachen.
                        </p>
                    </div>

                    {/* What NOT to enter */}
                    <div className="flex gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                        <p className="text-sm text-gray-200">Deine Daten gehören dir - 100% DSGVO-konform</p>
                    </div>
                </div>

                {/* AGB & Datenschutz */}
                <p className="text-center text-xs text-gray-400">
                    Mit der Nutzung von Walter Match bestätigst du, dass du die{' '}
                    <Link href="/terms" target="_blank" className="text-primary underline underline-offset-2">
                        AGB
                    </Link>{' '}
                    und die{' '}
                    <Link href="/privacy" target="_blank" className="text-primary underline underline-offset-2">
                        Datenschutzerklärung
                    </Link>{' '}
                    gelesen hast und damit einverstanden bist.
                </p>

                <AlertDialogFooter className="sm:justify-center">
                    <AlertDialogAction
                        onClick={handleAccept}
                        className="w-full bg-gradient-to-r from-primary to-secondary text-base font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-all duration-300 hover:scale-105 hover:shadow-xl sm:w-auto sm:px-8"
                    >
                        Lass uns loslegen!
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
